import { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Building2, Check, ShieldCheck, X } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { AuthShell } from '@/features/auth/LoginPage';
import { Button, ErrorState, GstChip, Spinner } from '@/features/common/ui';
import { approveLabel, initialSelection, listNames, roleLabel, toggle } from './consent-selection';

interface ConsentOrg {
  id: string;
  name: string;
  gstRegime: 'gst' | 'none';
  role: string;
}

interface ConsentContext {
  request: string;
  clientName: string;
  redirectHost: string;
  scopes: string[];
  orgs: ConsentOrg[];
  /** The client did not ask for write, so the person may grant "propose" here. */
  canChoosePropose?: boolean;
  /** Their choice for this client last time, when a grant is still live. */
  previousChoice?: 'propose' | 'read' | null;
}

/**
 * The consent screen. This is the feature.
 *
 * ── WHY IT IS A CHOICE OF TENANTS AND THEN A YES/NO ──
 * Fint is multi-tenant. The owner this was built for has an LLP and a
 * personal freelance book; an accountant using the practice features is often
 * a member of six businesses. An MCP connection is bound to a SET of them,
 * fixed at consent - `context.ts` is explicit that the org comes from the
 * credential and never from a tool argument. So this screen cannot be a plain
 * "allow?". If the tenant choice is buried or defaulted invisibly, somebody
 * connects Claude to the wrong client's books and does not find out until
 * Claude answers a question about the wrong business, confidently.
 *
 * ── A CHECKLIST OF WHAT IS LISTED, AND NOTHING THAT IS NOT ──
 * "Select all" ticks the books on the screen. There is deliberately no
 * "all my books, now and in future" - a grant whose reach grows after
 * consent is a grant the person did not give: the screen named three and the
 * connector would one day read a fourth. Adding a book later is a new
 * consent. That costs a click; the alternative costs this screen its meaning.
 *
 * The list is shown even when there is exactly one membership, because the
 * sentence "Claude will be able to see Acme Trading Pvt Ltd" is the sentence
 * worth reading - and because a user with one org this year may have five next
 * year, and a screen that only sometimes asks is a screen nobody reads.
 *
 * ── WHOSE NAME TO BELIEVE ──
 * Three things are shown, in descending order of trustworthiness, and the copy
 * says which is which:
 *
 *   1. The HOST of the redirect URI. The only part a client cannot lie about,
 *      because the authorization code goes there and nowhere else. The MCP
 *      security page requires it to be displayed.
 *   2. The client's own name, labelled as self-asserted. It arrived in an
 *      unauthenticated POST and anybody can send any string; presenting it as
 *      fact is how a phishing client borrows a brand.
 *   3. The logo. NOT rendered at all - the server stores it and never returns
 *      it. It is a remote image URL chosen by an anonymous registrant, which
 *      makes it an outbound fetch nobody asked for, a tracking pixel, and a way
 *      to paint a trusted brand onto an untrusted screen.
 */
export function ConsentPage() {
  const [params] = useSearchParams();
  const requestId = params.get('request') ?? '';
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [selected, setSelected] = useState<string[] | null>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);

  const context = useQuery({
    queryKey: ['oauth-consent', requestId],
    queryFn: () => api.get<ConsentContext>(`/oauth/authorize/context?request=${encodeURIComponent(requestId)}`),
    enabled: !!requestId && isAuthenticated,
    // A consent request lives ten minutes and is consumed on decision. Retrying
    // a stale one just delays telling the user to start again.
    retry: false,
  });

  /**
   * Ticked to start with: the one book, when there is exactly one, and
   * nothing when there are more. The active org used to be pre-selected; in
   * a checklist that reads as "already decided", which is the opposite of a
   * considered choice. `null` until the context arrives, so a slow response
   * cannot be mistaken for an empty selection.
   */
  useEffect(() => {
    if (!context.data || selected !== null) return;
    setSelected(initialSelection(context.data.orgs.map((o) => o.id)));
  }, [context.data, selected]);

  const chosenIds = selected ?? [];
  const total = context.data?.orgs.length ?? 0;

  // A native checkbox has three states and only two attributes; the third,
  // "some of them", is only reachable through the DOM property.
  useEffect(() => {
    const box = selectAllRef.current;
    if (!box) return;
    box.indeterminate = chosenIds.length > 0 && chosenIds.length < total;
  }, [chosenIds.length, total]);

  /**
   * ── READ, OR READ AND PROPOSE ──
   * The assistants' connectors ask for no scope, so what the connection may
   * do used to be decided by a server default the person never saw: read
   * only, and every proposing tool missing. The choice belongs here, on the
   * screen where they agree. "Propose" is preselected unless they chose
   * read-only for this client before - a deliberate choice is not silently
   * widened on a reconnect - and it never changes a client that asked for
   * write explicitly, which gets write as it always did.
   */
  const [allowPropose, setAllowPropose] = useState<boolean | null>(null);
  const proposeOffered = context.data?.canChoosePropose === true;
  const proposeChosen = allowPropose ?? context.data?.previousChoice !== 'read';

  const decide = useMutation({
    mutationFn: (approve: boolean) =>
      api.post<{ redirectTo: string }>('/oauth/authorize/decision', {
        requestId,
        approve,
        // The ticked set, one to many. On cancel it is empty and unread.
        orgIds: approve ? chosenIds : [],
        ...(approve && proposeOffered ? { allowPropose: proposeChosen } : {}),
      }),
    onSuccess: (result) => {
      // A full navigation, not a router push: the destination is the client's
      // own callback on another origin.
      window.location.assign(result.redirectTo);
    },
  });

  if (!isAuthenticated) {
    const next = `/oauth/consent?request=${encodeURIComponent(requestId)}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  if (!requestId) {
    return (
      <AuthShell title="Nothing to connect">
        <p className="text-sm text-muted-foreground">
          This page opens from an assistant asking to connect to Fint. Start again from the assistant.
        </p>
      </AuthShell>
    );
  }

  if (context.isLoading) {
    return (
      <AuthShell title="Connect an assistant">
        <Spinner />
      </AuthShell>
    );
  }

  if (context.isError || !context.data) {
    return (
      <AuthShell title="That request has expired">
        <ErrorState
          error={context.error}
          onRetry={() => {
            void context.refetch();
          }}
        />
        <p className="mt-3 text-sm text-muted-foreground">
          A connection request is good for ten minutes and can only be answered once. Start again from
          the assistant and this page will reopen.
        </p>
      </AuthShell>
    );
  }

  const { clientName, redirectHost, scopes, orgs } = context.data;
  const canWrite = scopes.includes('fint:write') || (proposeOffered && proposeChosen);
  const chosen = orgs.filter((o) => chosenIds.includes(o.id));
  const chosenNames = chosen.map((o) => o.name);
  const notChosenNames = orgs.filter((o) => !chosenIds.includes(o.id)).map((o) => o.name);
  const allChosen = orgs.length > 0 && chosen.length === orgs.length;
  const error = decide.error instanceof ApiError ? decide.error : undefined;

  if (orgs.length === 0) {
    return (
      <AuthShell title="No book to connect">
        <p className="text-sm text-muted-foreground">
          You are not an active member of any book in Fint, so there is nothing this assistant
          could be given access to.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Connect an assistant to Fint">
      <div className="space-y-5">
        {/*
          The redirect host first and largest, because it is the only fact on
          this screen that cannot be faked.
        */}
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
          <p>
            If you approve, Fint will send the connection to{' '}
            <span className="font-semibold text-foreground">{redirectHost}</span>.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            This application calls itself “{clientName}”. Fint cannot verify that name — only the
            address above, which is where the connection is actually sent. If you do not recognise it,
            cancel.
          </p>
        </div>

        <fieldset className="space-y-2">
          <legend className="flex items-center gap-1.5 text-sm font-medium">
            <Building2 className="size-4 text-muted-foreground" aria-hidden />
            Which books?
          </legend>
          <p id="org-choice-hint" className="text-xs text-muted-foreground">
            Tick every book this assistant may see. It will see those and no other — to add one
            later, connect again.
          </p>
          {/*
            Native checkboxes in a fieldset, not styled buttons: Tab reaches
            each one, Space toggles it, and the legend is read before the
            first option. The whole thing works without a mouse because the
            browser already knows how a checklist works.
          */}
          {orgs.length > 1 ? (
            <label className="flex cursor-pointer items-start gap-2 border-b border-border pb-2 text-sm">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allChosen}
                onChange={(event) => setSelected(event.target.checked ? orgs.map((o) => o.id) : [])}
                aria-describedby="select-all-hint"
                className="mt-0.5"
              />
              <span>
                Select all {orgs.length} listed
                <span id="select-all-hint" className="block text-xs font-normal text-muted-foreground">
                  Only these. A book you join later is not included.
                </span>
              </span>
            </label>
          ) : null}
          <div className="space-y-1.5">
            {orgs.map((org) => (
              <label
                key={org.id}
                className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-2.5 text-sm has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              >
                <input
                  type="checkbox"
                  name="consent-org"
                  value={org.id}
                  checked={chosenIds.includes(org.id)}
                  onChange={() => setSelected(toggle(chosenIds, org.id))}
                  aria-describedby="org-choice-hint"
                  className="mt-0.5"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{org.name}</span>
                    <GstChip regime={org.gstRegime} />
                  </span>
                  <span className="block text-xs text-muted-foreground">You are {roleLabel(org.role)} here</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {/*
          What it can do, written from the real tool surface rather than a
          generic sentence - and the visibility paragraph is not decoration.
          It is the promise docs/mcp.md makes and McpToolContext enforces, and
          this is where a person is entitled to read it before agreeing.
        */}
        <div className="space-y-2 text-sm">
          <p className="flex items-center gap-1.5 font-medium">
            <ShieldCheck className="size-4 text-muted-foreground" aria-hidden />
            What it will be able to do
          </p>
          <p>
            <span className="font-medium">Read</span> — invoices, customers, expenses, bank balances,
            GST returns, the audit trail and the review checks, for anything your role in{' '}
            {chosenNames.length ? listNames(chosenNames) : 'the books you tick'} can already see.
          </p>
          {proposeOffered ? (
            <fieldset className="space-y-2 rounded-md border border-border p-3">
              <legend className="px-1 text-sm font-medium">And beyond reading</legend>
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="radio"
                  name="propose"
                  className="mt-1"
                  checked={proposeChosen}
                  onChange={() => setAllowPropose(true)}
                  data-testid="consent-propose"
                />
                <span>
                  <span className="font-medium">Read and propose</span>
                  <span className="block text-muted-foreground">
                    It can draft expenses, income, invoices and customers into your Inbox, one at a time
                    or from a whole spreadsheet, and file records under projects and tags. Nothing it
                    proposes is in any figure until you confirm it in Fint.
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="radio"
                  name="propose"
                  className="mt-1"
                  checked={!proposeChosen}
                  onChange={() => setAllowPropose(false)}
                  data-testid="consent-read-only"
                />
                <span>
                  <span className="font-medium">Read only</span>
                  <span className="block text-muted-foreground">
                    It can answer questions and nothing else - no drafts, no queries, no files.
                  </span>
                </span>
              </label>
            </fieldset>
          ) : canWrite ? (
            <p>
              <span className="font-medium">Propose, never do</span> — draft expenses, income, invoices
              and customers into your Inbox; raise, answer and resolve review queries; attach a receipt
              to a record that already exists; revise the working copy of a quote. Nothing it proposes
              is in any figure until you confirm it in Fint.
            </p>
          ) : (
            <p className="text-muted-foreground">
              It is asking to read only. It will not be able to raise a query, attach a file or draft
              anything.
            </p>
          )}
          <p>
            <span className="font-medium">It cannot</span> issue or send a document, void or delete one,
            record a payment, confirm a proposal, change a category, lock a period, or change any
            amount on an invoice, a payment or a return.
          </p>
          {/*
            ── WHICH BOOKS IT SEES, STATED IN ONE SENTENCE ──
            This used to disclose that `fint_list_clients` could enumerate
            every business the person belonged to, whatever they had picked -
            a promise partly broken, said out loud. Under the multi-business
            grant that tool answers from the GRANT's org set, so the sentence
            is now the plain one: the ticked books are visible, the rest
            are not, and that includes any joined later.

            Two things about the wording. It is UNCONDITIONAL - shown with one
            business as with six, and with none ticked - because the grant
            outlives today's membership list, and somebody added to a second
            business next month would otherwise never see this sentence at all.
            And it NAMES the ticked and the unticked, because "the books
            above" is a sentence nobody checks and "Acme, and not Personal" is
            one they do.
          */}
          <p className="text-muted-foreground" data-testid="consent-visibility">
            {chosenNames.length ? (
              <>
                It will see <span className="font-medium text-foreground">{listNames(chosenNames)}</span>
                {notChosenNames.length ? <> and not {listNames(notChosenNames)}</> : null}.
              </>
            ) : (
              <>It will see only the books you tick.</>
            )}{' '}
            Any other book you belong to, now or later, stays invisible to this connection: it
            cannot list them, name them, or read anything in them.
          </p>
          <p className="text-xs text-muted-foreground">
            You can disconnect this at any time in Settings, without asking an administrator.
          </p>
        </div>

        {error ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error.message}
          </p>
        ) : null}

        {/*
          Disabled WITH a reason on the screen. A greyed button with no
          sentence is a button a person clicks three times and then blames
          the app; the sentence is tied to the button by aria-describedby so
          a screen reader hears why as well.
        */}
        {chosen.length === 0 ? (
          <p id="approve-hint" role="status" className="text-sm text-muted-foreground">
            Tick at least one book to connect.
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            className="flex-1"
            loading={decide.isPending && decide.variables === true}
            disabled={chosen.length === 0 || decide.isPending}
            aria-describedby={chosen.length === 0 ? 'approve-hint' : undefined}
            onClick={() => decide.mutate(true)}
          >
            <Check className="size-4" aria-hidden />
            {approveLabel(chosenNames)}
          </Button>
          {/*
            Cancel goes back to the client with `error=access_denied`, not to a
            closed tab - an assistant left hanging is a user who thinks Fint
            broke.
          */}
          <Button
            variant="outline"
            disabled={decide.isPending}
            onClick={() => decide.mutate(false)}
          >
            <X className="size-4" aria-hidden />
            Cancel
          </Button>
        </div>
      </div>
    </AuthShell>
  );
}
